import React, { useState, useCallback } from 'react';
import { InlineStack, Button, TextField } from '@shopify/polaris';

/**
 * T064: ApprovalToggle — select-toggle + reject/edit per field (FR-009).
 *
 * PENDING fields show a Select / ✓ Selected toggle button plus Edit and Reject actions.
 * APPROVED / APPLIED / REJECTED fields show a read-only status badge.
 *
 * The edit value is owned by the parent (editValues map) so it stays in sync
 * with the title caps toggle, which writes to the same value.
 */

interface ApprovalToggleProps {
  readonly previewId: string;
  readonly currentStatus: string;
  readonly isSelected: boolean;
  /** Current merchant edit for this field (from the parent), '' when none. */
  readonly editValue: string;
  readonly onToggleSelect: (previewId: string, checked: boolean) => void;
  readonly onEditChange?: (previewId: string, value: string) => void;
  readonly onReject: (previewId: string) => void;
  readonly isLoading?: boolean;
}

export function ApprovalToggle({
  previewId,
  currentStatus,
  isSelected,
  editValue,
  onToggleSelect,
  onEditChange,
  onReject,
  isLoading = false,
}: ApprovalToggleProps): React.JSX.Element {
  const [editing, setEditing] = useState(false);

  const handleEditValueChange = useCallback(
    (value: string) => {
      onEditChange?.(previewId, value);
    },
    [previewId, onEditChange],
  );

  const handleToggleEdit = useCallback(() => {
    if (editing) {
      // Cancelling — discard the edit from parent tracking
      onEditChange?.(previewId, '');
    }
    setEditing((prev) => !prev);
  }, [editing, previewId, onEditChange]);

  const handleReject = useCallback(() => {
    onReject(previewId);
    setEditing(false);
  }, [previewId, onReject]);

  const isApproved = currentStatus === 'APPROVED';
  const isRejected = currentStatus === 'REJECTED';
  const isPending = currentStatus === 'PENDING';

  return (
    <InlineStack gap="200" blockAlign="center" wrap>
      {isPending && (
        <>
          <Button
            onClick={() => onToggleSelect(previewId, !isSelected)}
            variant="primary"
            size="slim"
            disabled={isLoading}
          >
            {isSelected ? '✓ Selected' : 'Select'}
          </Button>
          <Button onClick={handleToggleEdit} size="slim" disabled={isLoading}>
            {editing ? 'Cancel edit' : 'Edit'}
          </Button>
          <Button onClick={handleReject} loading={isLoading} tone="critical" size="slim">
            Reject
          </Button>
        </>
      )}

      {isApproved && (
        <Button disabled size="slim" variant="primary">
          ✓ Approved
        </Button>
      )}

      {isRejected && (
        <Button disabled size="slim" tone="critical">
          ✗ Rejected
        </Button>
      )}

      {editing && (
        <TextField
          label="Your edit"
          labelHidden
          value={editValue}
          onChange={handleEditValueChange}
          autoComplete="off"
          placeholder="Type your custom value..."
        />
      )}
    </InlineStack>
  );
}

export default ApprovalToggle;
